import { Button, Callout, Intent } from '@blueprintjs/core';
import { SearchPreset } from '@retro-ui/api-contract';
import _ from 'lodash';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';

import { Preset } from '@f/components/pages/dashboard/settings/pages/preset/Preset';
import { SearchParameterForm } from '@f/components/shared/search-parameter-form';
import { useSearchParamsValidation } from '@f/components/shared/search-parameter-form/services/useSearchParamsValidation';
import { Select } from '@f/components/shared/select';
import { findReactionSearchPreferences } from '@f/services/reaction-search-preference/reaction-search-preference.actions';

import cls from './BatchSearchParametersForm.module.css';

export interface PresetData {
  presetName: string | null;
  searchParams: SearchPreset;
  warningMessage?: string;
}

interface BatchSearchParametersFormProps {
  presets: Preset[];
  presetData: PresetData;
  onChange: (data: PresetData) => void;
  searchParamsContainerRef: RefObject<Element>;
  toggleSearchParams: () => void;
}

export function BatchSearchParametersForm(props: BatchSearchParametersFormProps) {
  const { presets, presetData, onChange, searchParamsContainerRef, toggleSearchParams } = props;
  const dispatch = useDispatch();

  const [isSearchParamsFormVisible, setIsSearchParamsFormVisible] = useState(false);
  // const [presetOverride, setPresetOverride] = useState(null);
  const { validation } = useSearchParamsValidation(presetData.searchParams);

  const activePreset = useMemo(() => {
    if (presetData.presetName != null) {
      const preset = presets.find(preset => preset.name === presetData.presetName);
      if (preset) {
        return preset;
      }
    }
    const matchingPreset = presets.find(preset =>
      _.isEqual(preset.searchParams, presetData.searchParams)
    );
    if (matchingPreset) {
      return matchingPreset;
    }
    return null;
  }, [presetData, presets]);

  useEffect(() => {
    dispatch(findReactionSearchPreferences());
  }, []);

  const updateSearchParams = (nextParams: SearchPreset) => {
    const data: PresetData = {
      searchParams: nextParams,
      presetName: null,
    };
    const existingPreset = presets.find(preset => _.isEqual(preset.searchParams, nextParams));
    if (existingPreset) {
      data.presetName = existingPreset.name;
    }
    onChange(data);
  };

  const onParamsChange = (nextParams: SearchPreset) => {
    if (_.isEqual(presetData.searchParams, nextParams)) {
      return;
    }
    updateSearchParams(nextParams);
  };
  const onPresetChange = (preset: Preset) => {
    updateSearchParams(preset.searchParams);
  };

  return (
    <>
      <div>
        <Select
          key={activePreset?.id ?? null}
          large
          items={presets}
          activeItemId={activePreset?.id ?? null}
          resetOnActiveItemSelection={false}
          idField="id"
          labelField="displayName"
          allowNoSelection
          filterable={false}
          placeholder="Custom"
          rightIcon="caret-down"
          onChange={onPresetChange}
          disabled={presets.length === 1}
        />
        <Button
          className={cls.viewParamsButton}
          minimal
          text="View and modify parameters"
          icon="cog"
          active={isSearchParamsFormVisible}
          onClick={() => {
            setIsSearchParamsFormVisible(!isSearchParamsFormVisible);
            toggleSearchParams();
          }}
        />

        {isSearchParamsFormVisible &&
          searchParamsContainerRef.current &&
          presetData.searchParams &&
          createPortal(
            <SearchParameterForm
              groupClassName={cls.paramGroup}
              searchParams={presetData.searchParams}
              onChange={onParamsChange}
            />,
            searchParamsContainerRef.current
          )}
      </div>
      {validation.areDatasetsEmpty && (
        <Callout intent={Intent.DANGER} className={cls.error}>
          {validation.datasetsEmptyMessage}
        </Callout>
      )}
      {validation.isDatasetSizeLimitExceeded && (
        <Callout intent={Intent.DANGER} className={cls.error}>
          {validation.datasetSizeLimitExceededMessage}
        </Callout>
      )}
    </>
  );
}
